import { observable, action, runInAction } from "mobx";
import agent from "../agent";
import _ from "lodash";
import profileStore from "./profileStore";
import { ProfileType, ProfileRelationType } from "../types/enum";
import CacheHelper from '../helper/cache.js'
import { string } from "prop-types";
import commonStore from "./commonStore"
import { loadStaffAgentVenues, loadStaffVenueAgents, loadStaffVenueAgentRequests } from "../helper/staffAgentRoster";
class AgentVenueStore {
    staffAgentRosterGeneration = 0;
    staffAgentRosterContext = undefined;
    @observable invitationMessage = "";
    @observable manageSocialPost = false;
    @observable invitedId = undefined;
    @observable inviteeId = undefined;
    @observable invitedProfile = undefined;
    @observable manage = false

    @observable invitedVenues = [];

    @observable tableData = [];

    @observable agentVenueSocials = []
    @observable pendingAgentVenues = [];
    @observable agentVenues = [];

    @observable groupedPendingAgentVenues = [];
    @observable groupedAgentVenues = [];
    @observable isMoreAgentVenues = false;
    @observable agentVenuePage = 1;
    @observable highestLoadedPage = 1;
    @observable isLoadingVenues = false;

    @observable venueRosterSearchTerm = ""
    @observable isSearchingVenueRoster = false

    @observable venuePaginationState = {
        pageSize: 30,
        totalLoaded: 0,
        isLoadingAll: false,
        loadAllProgress: 0,
        loadAllCancelled: false
    };

    @action
    resetData() {
        this.staffAgentRosterGeneration += 1;
        this.staffAgentRosterContext = undefined;
        this.tableData = [];
        this.pendingAgentVenues = [];
        this.agentVenues = [];
        this.groupedPendingAgentVenues = [];
        this.groupedAgentVenues = [];
        this.isMoreAgentVenues = false
        this.agentVenuePage = 1
        this.highestLoadedPage = 1
        this.venueRosterSearchTerm = "";
        this.isSearchingVenueRoster = false;
    }

    @action
    resetAgentVenues() {
        this.agentVenues = [];
        this.isMoreAgentVenues = false
        this.agentVenuePage = 1
        this.highestLoadedPage = 1
        this.resetVenuePaginationState();
    }

    @action
    resetVenuePaginationState() {
        this.venuePaginationState = {
            pageSize: 30,
            totalLoaded: 0,
            isLoadingAll: false,
            loadAllProgress: 0,
            loadAllCancelled: false
        };
    }

    @action
    setAgentVenuePage(page) {
        this.agentVenuePage = parseInt(page, 10) || 1;
    }

    @action
    setIsLoadingVenues(loading) {
        this.isLoadingVenues = loading;
    }

    @action
    setHighestLoadedPage(page) {
        if (page > this.highestLoadedPage) {
            this.highestLoadedPage = page;
        }
    }

    @action
    cancelLoadAllVenues() {
        this.venuePaginationState.loadAllCancelled = true;
        this.venuePaginationState.isLoadingAll = false;
        if (this.agentVenues.length > 0) {
            this.isMoreAgentVenues = true;
        }
    }

    @action
    updateVenueContactData(agentVenueId, contactData) {
        const venueIndex = this.agentVenues.findIndex(v => v.id === agentVenueId);
        if (venueIndex !== -1) {
            this.agentVenues[venueIndex] = {
                ...this.agentVenues[venueIndex],
                ...contactData
            };
        }
    }

    @action
    async loadAllVenues(profileId, isConsultant = false) {
        const currentProfile = profileStore.getCurrentProfile();
        if (!profileId) {
            profileId = currentProfile?.id;
        }

        const cachedState = {
            venues: [...this.agentVenues],
            highestPage: this.highestLoadedPage,
            currentPage: this.agentVenuePage,
            isMore: this.isMoreAgentVenues
        };
        
        runInAction(() => {
            this.venuePaginationState.isLoadingAll = true;
            this.venuePaginationState.loadAllCancelled = false;
            this.venuePaginationState.loadAllProgress = 0;
            this.agentVenuePage = 1;
            this.highestLoadedPage = 1;
        });

        const { pageSize } = this.venuePaginationState;
        const CONCURRENT_REQUESTS = 4;
        let allVenues = [];
        let currentBatchStart = 1;
        let hasMore = true;

        try {
            while (hasMore && !this.venuePaginationState.loadAllCancelled) {
                const pageNumbers = [];
                for (let i = 0; i < CONCURRENT_REQUESTS; i++) {
                    pageNumbers.push(currentBatchStart + i);
                }

                const apiMethod = isConsultant 
                    ? agent.AgentVenues.getConsultantVenueRosterV2
                    : agent.AgentVenues.getMyVenueRosterV2;

                const promises = pageNumbers.map(pageNum => 
                    apiMethod(profileId, pageSize, pageNum)
                );

                const results = await Promise.all(promises);

                let batchHasMore = false;
                let maxPageInBatch = 1;
                for (let i = 0; i < results.length; i++) {
                    if (this.venuePaginationState.loadAllCancelled) break;

                    const result = results[i];
                    const pageNum = pageNumbers[i];
                    const response = result.data;
                    if (response && Array.isArray(response) && response.length > 0) {
                        for (const venue of response) {
                            await profileStore.mapAdditionalProfileDataAsync(venue.venue);
                            if (isConsultant && venue.agent) {
                                await profileStore.mapAdditionalProfileDataAsync(venue.agent);
                            }
                        }
                        allVenues = allVenues.concat(response);
                        maxPageInBatch = Math.max(maxPageInBatch, pageNum);
                    }
                    if (result.isMore) {
                        batchHasMore = true;
                    }
                }
                
                hasMore = batchHasMore;
                currentBatchStart += CONCURRENT_REQUESTS;

                runInAction(() => {
                    this.agentVenues = [...allVenues];
                    this.highestLoadedPage = Math.max(this.highestLoadedPage, maxPageInBatch);
                    this.venuePaginationState.totalLoaded = allVenues.length;
                    const estimatedTotal = allVenues.length + (hasMore ? pageSize : 0);
                    this.venuePaginationState.loadAllProgress = estimatedTotal > 0 
                        ? Math.min(95, (allVenues.length / estimatedTotal) * 100) 
                        : 0;
                });
            }

            runInAction(() => {
                if (this.venuePaginationState.loadAllCancelled) {
                    if (this.agentVenues.length === 0 && cachedState.venues.length > 0) {
                        this.agentVenues = cachedState.venues;
                        this.highestLoadedPage = cachedState.highestPage;
                        this.agentVenuePage = cachedState.currentPage;
                        this.isMoreAgentVenues = cachedState.isMore;
                    } else if (this.agentVenues.length > 0) {
                        this.isMoreAgentVenues = true;
                    }
                } else {
                    this.agentVenues = [...allVenues];
                    this.isMoreAgentVenues = false;
                    this.venuePaginationState.loadAllProgress = 100;
                }
                this.venuePaginationState.isLoadingAll = false;
            });

            return this.agentVenues;
        } catch (error) {
            console.error("Load all venues failed:", error);
            runInAction(() => {
                this.venuePaginationState.isLoadingAll = false;
                if (cachedState.venues.length > 0) {
                    this.agentVenues = cachedState.venues;
                    this.highestLoadedPage = cachedState.highestPage;
                    this.agentVenuePage = cachedState.currentPage;
                    this.isMoreAgentVenues = cachedState.isMore;
                }
            });
            throw error;
        }
    }

    @action
    pushTableData(row) {
        this.tableData.push(row);
    }

    @observable
    dayToBook = {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false
    };

    @observable
    dayToBookVenueInvitation = {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true
    };

    @observable editedAgentVenue = undefined;

    cacheDuration = 60 * 60 * 1000; // 30 minutes in milliseconds


    @action
    setEditedAgentVenue(agentVenue) {
        if (agentVenue) {
            this.editedAgentVenue = Object.assign({}, agentVenue);
        } else {
            this.editedAgentVenue = undefined;
        }
    }

    @action
    toggleEditedAgentVenueBooking(value) {
        this.editedAgentVenue[value] = !this.editedAgentVenue[value];
    }

    @action
    setDayToBook(rules) {
        this.dayToBook = rules;
    }

    @action
    setManage(value) {
        this.manage = value;
    }

    @action
    setSocialPost(value){
        this.manageSocialPost = value
    }

    editAgentVenue(agentVenue) {
        const profileId = profileStore.getCurrentProfile()?.id;
        if (!profileId) {
            return Promise.reject(new Error("Select a profile before updating booking days."));
        }
        return agent.AgentVenues.edit({ agentVenue, profileId });
    }

    @action
    submit(agentVenues) {
        if (!agentVenues) {
            agentVenues = this.agentVenues;
        }

        var data = agentVenues.map(agentVenue => {
            return {
                id: agentVenue.id,
                manage: agentVenue.manage,
                monday: agentVenue.monday,
                tuesday: agentVenue.tuesday,
                wednesday: agentVenue.wednesday,
                thursday: agentVenue.thursday,
                friday: agentVenue.friday,
                saturday: agentVenue.saturday,
                sunday: agentVenue.sunday
            };
        });

        return Promise.all(data.map(agentVenue => this.editAgentVenue(agentVenue))).then(
            action(results => {
                const response = results.reduce((all, result) => all.concat(result), []);
                response.forEach(res => {
                    this.agentVenues = this.agentVenues.map(data => {
                        if (data.id === res.id) {
                            data.monday = res.monday;
                            data.tuesday = res.tuesday;
                            data.wednesday = res.wednesday;
                            data.thursday = res.thursday;
                            data.friday = res.friday;
                            data.saturday = res.saturday;
                            data.sunday = res.sunday;
                            data.manage = res.manage;
                            data.accepted = true;
                        }
                        return data;
                    });
                });

                this.groupedPendingAgentVenues = _.chain(this.agentVenues)
                    .groupBy("venueId")
                    .toPairs()
                    .map(function (pair) {
                        return _.zipObject(["venue", "data"], pair);
                    })
                    .value();

                return this.agentVenues;
            })
        );
    }

    @action
    changeManagePermision(value, id) {
        this.agentVenues = this.agentVenues.map(agentVenue => {
            if (agentVenue.id === id) {
                agentVenue.manage = value;
            }
            return agentVenue;
        });
    }

    @action
    updateManagePermision(value, id) {
        var agentVenue = undefined;

        this.agentVenues.forEach(data => {
            if (data.id === id && !agentVenue) {
                agentVenue = data;
            }
        });

        var requestData = {
                id: agentVenue.id,
                manage: value,
                manageSocialPost: agentVenue.manageSocialPost,
                monday: agentVenue.monday,
                tuesday: agentVenue.tuesday,
                wednesday: agentVenue.wednesday,
                thursday: agentVenue.thursday,
                friday: agentVenue.friday,
                saturday: agentVenue.saturday,
                sunday: agentVenue.sunday
        };

        return this.editAgentVenue(requestData).then(
            action(response => {
                this.agentVenues = this.agentVenues.map(data => {
                    if (data.id === id) {
                        data.manage = value;
                    }
                    return data;
                });
                this.groupedPendingAgentVenues = _.chain(this.agentVenues)
                    .groupBy("venueId")
                    .toPairs()
                    .map(function (pair) {
                        return _.zipObject(["venue", "data"], pair);
                    })
                    .value();
                return this.agentVenues;
            })
        );
    }

    @action
    updateDefaultIncludeSuper(agentVenueId, value,profileId) {
        return agent.AgentVenues.updateSuperInclude(agentVenueId,{defaultIncludeSuper: value, profileId: profileId}).then(
            action(response => {
                this.agentVenues.forEach(data => {
                    if (data.id === agentVenueId) {
                        data.defaultIncludeSuper = value;
                    }
                });
            })
        );
    }

    @action
    handleToggleManageSocialPost(value, id) {
        var agentVenue = undefined;

        this.agentVenues.forEach(data => {
            if (data.id === id && !agentVenue) {
                agentVenue = data;
            }
        });

        var requestData = {
                id: agentVenue.id,
                manage: agentVenue.monday,
                manageSocialPost: value,
                monday: agentVenue.monday,
                tuesday: agentVenue.tuesday,
                wednesday: agentVenue.wednesday,
                thursday: agentVenue.thursday,
                friday: agentVenue.friday,
                saturday: agentVenue.saturday,
                sunday: agentVenue.sunday
        };

        return this.editAgentVenue(requestData).then(
            action(response => {
                this.agentVenues = this.agentVenues.map(data => {
                    if (data.id === id) {
                        data.manageSocialPost = value;
                    }
                    return data;
                });
                this.groupedPendingAgentVenues = _.chain(this.agentVenues)
                    .groupBy("venueId")
                    .toPairs()
                    .map(function (pair) {
                        return _.zipObject(["venue", "data"], pair);
                    })
                    .value();
                return this.agentVenues;
            })
        );
    }
    //get agent's venue

    @action
    getById(id) {
        return agent.AgentVenues.getById(id).then(response => {
            profileStore.mapAdditionalProfileData(response.agent);
            profileStore.mapAdditionalProfileData(response.venue);
            return response;
        });
    }

    @action
    getAgentVenueRequest() {
        return agent.ProfileRelationship.getAgentVenueRequets().then(
            action(async response => {
                if (response !== 'undefined' && response !== undefined && typeof (response) !== string) {
                    this.pendingAgentVenues = response.length > 0 ? response : [];
                    for (let i = 0; i < response.length; i++) {
                        await profileStore.mapAdditionalProfileDataAsync(response[i].primaryProfile);
                        await profileStore.mapAdditionalProfileDataAsync(response[i].secondaryProfile);
                        runInAction(() => {
                            this.pendingAgentVenues[i] = response[i]
                        })
                    }
                }
                return this.pendingAgentVenues;
            })
        );
    }
    

    @action
    getAgentVenueSimple(agentId) {
        const currentProfile = profileStore.getCurrentProfile();
        if (!agentId) {
            if (currentProfile != undefined) {
                agentId = currentProfile.id;
            }
        }
        const cachedData = localStorage.getItem("agentVenueRoster");
        const cachedTimestamp = localStorage.getItem(`agentVenueRoster_timestamp`);
        const resetStorage = localStorage.getItem(`resetAgentVenueRoster`);

        if (cachedData && cachedTimestamp && !resetStorage) {
            const timeElapsed = Date.now() - parseInt(cachedTimestamp, 10);
            if (timeElapsed < this.cacheDuration) {
                this.agentVenues = JSON.parse(cachedData)
                return Promise.resolve(this.agentVenues);
            }else{
                return agent.AgentVenues.getMyVenueRosterSimple(agentId).then(
                    action(async response => {
                        let mappedData = []
                        for (var i = 0; i < response.length; i++) {
                            await profileStore.mapAdditionalProfileDataAsync(response[i].venue)
                            mappedData.push(response[i])
                            
                        }
                        runInAction(() => {
                            this.agentVenues = _.orderBy(mappedData, x=>x.venue.profileNameString)
                            localStorage.setItem("agentVenueRoster", JSON.stringify(this.agentVenues));
                            localStorage.setItem(`agentVenueRoster_timestamp`, Date.now().toString());
               
                        })
                        return Promise.resolve(this.agentVenues);
                    })
                )
            }
            
  
             
        }else{
            if(resetStorage){
                commonStore.resetLocalStorageContainingWords(["agentVenueRoster", "resetAgentVenueRoster"])
            }

            return agent.AgentVenues.getMyVenueRosterSimple(agentId).then(
                action(async response => {
                    let mappedData = []
                    for (var i = 0; i < response.length; i++) {
                        await profileStore.mapAdditionalProfileDataAsync(response[i].venue)
                        mappedData.push(response[i])
                        
                    }
                    runInAction(() => {
                        this.agentVenues = _.orderBy(mappedData, x=>x.venue.profileNameString)
                        localStorage.setItem("agentVenueRoster", JSON.stringify(this.agentVenues));
                        localStorage.setItem(`agentVenueRoster_timestamp`, Date.now().toString());
           
                    })
                    return Promise.resolve(this.agentVenues);
                })
            )
        }
       
    }
    

    @action
    getMyVenueRosterSocial(agentId) {
        const currentProfile = profileStore.getCurrentProfile();
        if (!agentId) {
            if (currentProfile != undefined) {
                agentId = currentProfile.id;
            }else{
                return
            }
            
        }
        return agent.AgentVenues.getMyVenueRosterSocial(agentId).then(
            action(async response => {
                if (response !== 'undefined' && response !== undefined && typeof (response) !== string) {
                    let mappedData = []
                    for (var i = 0; i < response.length; i++) {
                        await profileStore.mapAdditionalProfileDataAsync(response[i].venue)
                        mappedData.push(response[i])
                        
                    }
                    runInAction(() => {
                        this.agentVenueSocials = mappedData
                        return this.agentVenueSocials
                    })
                } else this.agentVenueSocials = []
            })
        );
    }
    
    @action
    getMyVenueRosterV2(agentId,limit,page) {
        const currentProfile = profileStore.getCurrentProfile();
        if (!agentId) {
            if (currentProfile != undefined) {
                agentId = currentProfile.id;
            }else{
                return
            }
            
        }
        return agent.AgentVenues.getMyVenueRosterV2(agentId,limit,page).then(
            action(async result => {
                var response = result.data
                if (response !== 'undefined' && response !== undefined && typeof (response) !== string) {
                    let mappedData = []
                    for (var i = 0; i < response.length; i++) {
                        await profileStore.mapAdditionalProfileDataAsync(response[i].venue)
                        mappedData.push(response[i])
                    }
                    
                    runInAction(() => {
                        this.agentVenues = this.agentVenues.concat(mappedData)
                        this.isMoreAgentVenues = result.isMore
                        return this.agentVenues
                    })
                } else this.agentVenues = []
            })
        );
    }

    @action
    setAgentVenuePage(page){
        this.agentVenuePage = page
    }

    @action
    getAgentVenue(agentId) {
        const currentProfile = profileStore.getCurrentProfile();
        if (!agentId) {
            if (currentProfile != undefined) {
                agentId = currentProfile.id;
            }else{
                return
            }
            
        }
        return agent.AgentVenues.getMyVenueRoster(agentId).then(
            action(async response => {
                if (response !== 'undefined' && response !== undefined && typeof (response) !== string) {
                    let mappedData = []
                    for (var i = 0; i < response.length; i++) {
                        await profileStore.mapAdditionalProfileDataAsync(response[i].venue)
                        mappedData.push(response[i])
                    }
                    runInAction(() => {
                        this.agentVenues = _.orderBy(mappedData, x=>x.venue.profileNameString)
                        return this.agentVenues
                    })
                   
                } else this.agentVenues = []
            })
        );
    }

    @action
    getAgentVenueForMyGigs(agentId) {
        const currentProfile = profileStore.getCurrentProfile();

        if (!agentId) {
            if (currentProfile != undefined) {
                agentId = currentProfile.id;
            }
        }
        return agent.AgentVenues.getMyVenueRosterSimple(agentId).then(
            action(async response => {
                let result = [];
                if (response !== 'undefined' && response !== undefined && typeof (response) !== string) {
                    this.agentVenues = response ? response : []
                    for (var i = 0; i < response.length; i++) {
                        await profileStore.mapAdditionalProfileDataAsync(response[i].venue);
                        result.push(response[i])

                    }
                    runInAction(() => {
                        this.agentVenues = _.orderBy(result, x=>x.venue.profileNameString)
                        return this.agentVenues
                    })

                }
                return response;

            })
        );
    }

    //get venue's agent

    @action
    getConsultantVenueRoster(consultantId) {
        if (!consultantId) {
            const profile = profileStore.getCurrentProfile();
            
            consultantId = profile.id;
        }
        return agent.AgentVenues.getConsultantVenueRoster(consultantId).then(
            action(async response => {
                if (response !== 'undefined' && response !== undefined && typeof (response) !== string) {
                    this.agentVenues = response ? response : []
                    for (var i = 0; i < response.length; i++) {
                        await profileStore.mapAdditionalProfileDataAsync(response[i].venue)
                        await profileStore.mapAdditionalProfileDataAsync(response[i].agent)
                        runInAction(() => {
                            this.agentVenues[i] = response[i]
                        })
                    }
                }
            })
        );
    }

    @action
    getConsultantVenueRosterV2(consultantId, limit, page) {
        if (!consultantId) {
            const profile = profileStore.getCurrentProfile();
            
            consultantId = profile.id;
        }
        return agent.AgentVenues.getConsultantVenueRosterV2(consultantId, limit, page).then(
            action(async result => {
                var response = result.data
                if (response !== 'undefined' && response !== undefined && typeof (response) !== string) {
                    var mappedData = []
                    for (var i = 0; i < response.length; i++) {
                        await profileStore.mapAdditionalProfileDataAsync(response[i].venue)
                        await profileStore.mapAdditionalProfileDataAsync(response[i].agent)
                        mappedData.push(response[i])
                    }
                    runInAction(() => {
                        this.agentVenues = this.agentVenues.concat(mappedData)
                        this.isMoreAgentVenues = result.isMore
                    })
                }
            })
        );
    }

    @action
    setVenueRosterSearchTerm(term) {
        this.venueRosterSearchTerm = term;
    }

    @action
    clearVenueRosterSearch() {
        this.venueRosterSearchTerm = "";
        this.isSearchingVenueRoster = false;
    }

    @action
    resetVenueRosterForSearch() {
        this.agentVenues = [];
        this.isMoreAgentVenues = false;
        this.agentVenuePage = 1;
        this.highestLoadedPage = 1;
        this.resetVenuePaginationState();
    }

    @action
    searchVenueRoster(profileId, isConsultant = false) {
        const currentProfile = profileStore.getCurrentProfile();
        if (!profileId) {
            profileId = currentProfile?.id;
        }
        this.isSearchingVenueRoster = true;
        this.resetVenueRosterForSearch();

        const searchTerm = this.venueRosterSearchTerm.trim() || undefined;

        if (!searchTerm) {
            this.isSearchingVenueRoster = false;
            return Promise.resolve([]);
        }

        const apiMethod = isConsultant
            ? agent.AgentVenues.getConsultantVenueRosterV2WithSearch
            : agent.AgentVenues.getMyVenueRosterV2WithSearch;

        return apiMethod(profileId, 30, 1, searchTerm)
            .then(
                action(async result => {
                    var response = result?.data ? result?.data : [];
                    var mappedData = [];
                    for (let data of response) {
                        await profileStore.mapAdditionalProfileDataAsync(data.venue);
                        if (isConsultant && data.agent) {
                            await profileStore.mapAdditionalProfileDataAsync(data.agent);
                        }
                        mappedData.push(data);
                    }
                    runInAction(() => {
                        this.agentVenues = mappedData;
                        this.isMoreAgentVenues = result?.isMore || false;
                        this.isSearchingVenueRoster = false;
                        this.agentVenuePage = 1;
                    });
                    return this.agentVenues;
                })
            )
            .catch(action(err => {
                this.isSearchingVenueRoster = false;
                throw err;
            }));
    }

    @action
    searchVenueRosterNextPage(profileId, page, isConsultant = false) {
        const currentProfile = profileStore.getCurrentProfile();
        if (!profileId) {
            profileId = currentProfile?.id;
        }

        const searchTerm = this.venueRosterSearchTerm.trim() || undefined;
        if (!searchTerm) {
            return Promise.resolve([]);
        }

        this.isLoadingVenues = true;

        const apiMethod = isConsultant
            ? agent.AgentVenues.getConsultantVenueRosterV2WithSearch
            : agent.AgentVenues.getMyVenueRosterV2WithSearch;

        return apiMethod(profileId, 30, page, searchTerm)
            .then(
                action(async result => {
                    var response = result?.data ? result?.data : [];
                    var mappedData = [];
                    for (let data of response) {
                        await profileStore.mapAdditionalProfileDataAsync(data.venue);
                        if (isConsultant && data.agent) {
                            await profileStore.mapAdditionalProfileDataAsync(data.agent);
                        }
                        mappedData.push(data);
                    }
                    runInAction(() => {
                        this.agentVenues = this.agentVenues.concat(mappedData);
                        this.isMoreAgentVenues = result?.isMore || false;
                    });
                    return this.agentVenues;
                })
            )
            .finally(
                action(() => {
                    this.isLoadingVenues = false;
                })
            );
    }

    getConsultantVenueRosterSimple(consultantId) {
        if (!consultantId) {
            const profile = profileStore.getCurrentProfile();
            
            consultantId = profile.id;
        }
        return agent.AgentVenues.getConsultantVenueRosterSimple(consultantId).then(
            action(async response => {
                if (response !== 'undefined' && response !== undefined && typeof (response) !== string) {
                    this.result = []
                    for (var i = 0; i < response.length; i++) {
                        await profileStore.mapAdditionalProfileDataAsync(response[i].venue)
                        await profileStore.mapAdditionalProfileDataAsync(response[i].agent)
                        this.result.push(response[i])
                    }
                     runInAction(() => {
                        this.agentVenues = _.orderBy(this.result, x=>x.venue.profileNameString)
                        return this.agentVenues
                    })
                }
            })
        );
    }

    @action
    getVenueAgent(venueId) {
        return Promise.all([
            agent.ProfileRelationship.getVenueAgentRequest(venueId).then(
                action(async response => {
                    var mappedPending = [];
                    for (var i = 0; i < response.length; i++) {
                        var data = response[i];
                        await profileStore.mapAdditionalProfileDataAsync(data.primaryProfile);
                        await profileStore.mapAdditionalProfileDataAsync(data.secondaryProfile);
                        data.accepted = false;
                        mappedPending.push(data)
                    }
                    runInAction(() => {
                        this.pendingAgentVenues = mappedPending
                        return mappedPending;
                    })
                })
            ),
            agent.AgentVenues.getMyVenuesAgent(venueId).then(
                action(async response => {
                    var agentVen = []
                    for (var i = 0; i < response.length; i++) {
                        var data = response[i];
                        await profileStore.mapAdditionalProfileDataAsync(data.agent);
                        await profileStore.mapAdditionalProfileDataAsync(data.venue);
                        data.accepted = true;
                        agentVen.push(data);
                    }
                    runInAction(() => {
                        this.agentVenues = _.orderBy(agentVen, x=>x.venue.profileNameString)
                        return agentVen
                    })
                })
            )
        ]);
    }

    @action
    getActiveVenueAgent(venueId) {
        return agent.AgentVenues.getMyVenuesAgent(venueId).then(
            action(response => {
                this.agentVenues = response.map(data => {
                    profileStore.mapAdditionalProfileData(data.agent);
                    profileStore.mapAdditionalProfileData(data.venue);
                    data.accepted = true;
                    return data;
                });
                return this.agentVenues;
            })
        );
    }

    @action
    clearStaffAgentRoster() {
        this.resetData();
    }

    isCurrentStaffRoster(staffId, generation) {
        const profileId = profileStore.getCurrentProfile()?.id;
        return profileId != null && String(profileId) === String(staffId)
            && generation === this.staffAgentRosterGeneration;
    }

    @action
    async loadStaffAgentRoster(staffId) {
        if (!staffId || !this.isCurrentStaffRoster(staffId, this.staffAgentRosterGeneration)) {
            throw new Error("Select a staff profile before loading venue agents.");
        }
        this.resetData();
        const generation = this.staffAgentRosterGeneration;
        const venues = await loadStaffAgentVenues();
        if (!this.isCurrentStaffRoster(staffId, generation)) {
            throw new Error("The staff profile changed while loading venue agents.");
        }
        const [agentVenues, pendingAgentVenues] = await Promise.all([
            loadStaffVenueAgents(venues),
            loadStaffVenueAgentRequests(venues)
        ]);
        if (!this.isCurrentStaffRoster(staffId, generation)) {
            throw new Error("The staff profile changed while loading venue agents.");
        }
        runInAction(() => {
            this.staffAgentRosterContext = { staffId, venues };
            this.agentVenues = agentVenues;
            this.pendingAgentVenues = pendingAgentVenues;
            this.groupedPendingAgentVenues = _.chain(agentVenues)
                .groupBy("venueId")
                .toPairs()
                .map(pair => _.zipObject(["venue", "data"], pair))
                .value();
        });
        return venues.slice();
    }

    @action
    getStaffVenueAgentRequest(staffId) {
        const currentProfile = profileStore.getCurrentProfile();

        if (!staffId) {
            staffId = currentProfile.id;
        }

        const context = this.staffAgentRosterContext;
        if (context && String(context.staffId) === String(staffId)) {
            const generation = this.staffAgentRosterGeneration;
            return loadStaffVenueAgentRequests(context.venues).then(action(rows => {
                if (this.isCurrentStaffRoster(staffId, generation)) {
                    this.pendingAgentVenues = rows;
                }
                return rows;
            }));
        }

        return agent.ProfileRelationship.getStaffVenuesAgentRequests(staffId).then(
            action(async response => {
                if (response && response.length > 0 && response !== undefined) {
                    var mapped = [];
                    for (var i = 0; i < response.length; i++) {
                        var data = response[i]
                        await profileStore.mapAdditionalProfileDataAsync(data.primaryProfile);
                        await profileStore.mapAdditionalProfileDataAsync(data.secondaryProfile);
                        data.accepted = true;
                        mapped.push(data)
                    }
                    runInAction(() => {
                        this.pendingAgentVenues = mapped
                        return mapped;
                    })
                } else {
                    this.pendingAgentVenues = []
                    return mapped;
                }


            })

        );
    }

    @action
    getStaffVenueAgent(staffId) {
        const currentProfile = profileStore.getCurrentProfile();

        if (!staffId) {
            staffId = currentProfile.id;
        }

        const context = this.staffAgentRosterContext;
        if (context && String(context.staffId) === String(staffId)) {
            const generation = this.staffAgentRosterGeneration;
            return loadStaffVenueAgents(context.venues).then(action(rows => {
                if (this.isCurrentStaffRoster(staffId, generation)) {
                    this.agentVenues = rows;
                    this.groupedPendingAgentVenues = _.chain(rows)
                        .groupBy("venueId")
                        .toPairs()
                        .map(pair => _.zipObject(["venue", "data"], pair))
                        .value();
                }
                return rows;
            }));
        }

        return agent.AgentVenues.getStaffVenuesAgent(staffId).then(
            action(async response => {
                var mapped = await Promise.all(_.map(response, async data => {
                    await profileStore.mapAdditionalProfileDataAsync(data.agent);
                    await profileStore.mapAdditionalProfileDataAsync(data.venue);
                    data.accepted = true;
                    return data;
                }));
                runInAction(() => {
                    this.agentVenues = mapped
                    this.groupedPendingAgentVenues = _.chain(mapped)
                        .groupBy("venueId")
                        .toPairs()
                        .map(function (pair) {
                            return _.zipObject(["venue", "data"], pair);
                        })
                        .value();

                    return this.agentVenues;
                })


            })
        );
    }

    @action
    deleteAgentVenues(ids) {
        let { profile } = profileStore;
        return agent.AgentVenues.deleteAgentVenues(ids, profile.id).then(
            action(response => {
                if(profile.type === ProfileType.Agent.ordinal){
                    localStorage.setItem(`resetAgentVenueRoster`, true);
                }
                localStorage.setItem(`resetAgentVenueRoster`, true);
                response.forEach(data => {
                    _.remove(this.agentVenues, agentVenue => {
                        return agentVenue.id === data.id;
                    });
                    return data;
                });
                return response;
            })
        );
    }

    @action
    deleteAgentVenue(id) {
        let { profile } = profileStore;
        return agent.AgentVenues.deleteAgentVenue(id, profile.id).then(
            action(response => {
                if(profile.type === ProfileType.Agent.ordinal){
                    localStorage.setItem(`resetAgentVenueRoster`, true);
                }
                
                this.agentVenues = this.agentVenues.filter(x => x.id !== id);
                this.groupedPendingAgentVenues = _.chain(this.agentVenues)
                    .groupBy("venueId")
                    .toPairs()
                    .map(function (pair) {
                        return _.zipObject(["venue", "data"], pair);
                    })
                    .value();
                return response;
            })
        );
    }

    //invitation
    @action
    acceptRequest(request) {
        return agent.ProfileRelationship.accept(request).then(
            action(response => {
                localStorage.setItem(`resetAgentVenueRoster`, true);
                if (this.pendingAgentVenues.length <= 1) this.pendingAgentVenues = []
                if (this.agentVenues.length <= 1) this.agentVenues = []
                return response;
            })
        );
    }

    @action
    venueAcceptAgentRequest(request) {
        request.rules = JSON.stringify(this.dayToBook);
        return agent.ProfileRelationship.accept(request).then(
            action(response => {
                this.getVenueAgent(response.secondaryProfileId);
                return response;
            })
        );
    }

    @action
    staffAcceptAgentRequest(request) {
        const staffId = profileStore.getCurrentProfile()?.id;
        const generation = this.staffAgentRosterGeneration;
        request.rules = JSON.stringify(this.dayToBook);
        return agent.ProfileRelationship.accept(request).then(
            async response => {
                if (!this.isCurrentStaffRoster(staffId, generation)) return response;
                try {
                    const context = this.staffAgentRosterContext;
                    if (context && String(context.staffId) === String(staffId)) {
                        const [agentVenues, pendingAgentVenues] = await Promise.all([
                            loadStaffVenueAgents(context.venues),
                            loadStaffVenueAgentRequests(context.venues)
                        ]);
                        runInAction(() => {
                            if (this.isCurrentStaffRoster(staffId, generation)) {
                                this.agentVenues = agentVenues;
                                this.pendingAgentVenues = pendingAgentVenues;
                                this.groupedPendingAgentVenues = _.chain(agentVenues)
                                    .groupBy("venueId")
                                    .toPairs()
                                    .map(pair => _.zipObject(["venue", "data"], pair))
                                    .value();
                            }
                        });
                    } else {
                        await Promise.all([this.getStaffVenueAgentRequest(staffId), this.getStaffVenueAgent(staffId)]);
                    }
                    return response;
                } catch (error) {
                    if (!this.isCurrentStaffRoster(staffId, generation)) return response;
                    const refreshError = new Error("The request was accepted, but agents could not be reloaded. Please retry loading the roster.");
                    refreshError.requestAccepted = true;
                    throw refreshError;
                }
            }
        );
    }

    @action
    deleteRequestByStaff(request) {
        if (request.primaryProfile.type === ProfileType.Venue.ordinal) {
            return agent.ProfileRelationship.remove(request.id).then(
                action(response => {
                    this.pendingAgentVenues = this.pendingAgentVenues.filter(x => x.id !== request.id);
                    return this.pendingAgentVenues;
                })
            );
        } else {
            return this.declineAgentRequest(request.id);
        }
    }

    @action
    declineAgentRequest(requestId) {
        return agent.ProfileRelationship.decline(requestId).then(
            action(response => {
                this.pendingAgentVenues = this.pendingAgentVenues.filter(x => x.id !== response.id);
                return response;
            })
        );
    }

    @action
    declineAgentRequests(ids) {
        return agent.ProfileRelationship.declineMany(ids).then(
            action(response => {
                response.forEach(data => {
                    this.pendingAgentVenues = this.pendingAgentVenues.filter(x => x.id !== data.id);
                    return data;
                });
                return response;
            })
        );
    }

    @action
    removeAgentRequests(ids) {
        return agent.ProfileRelationship.removeMany(ids).then(
            action(response => {
                response.forEach(data => {
                    this.pendingAgentVenues = this.pendingAgentVenues.filter(x => x.id !== data.id);
                    return data;
                });
                return response;
            })
        );
    }

    @action
    resetInvitationField() {
        this.invitedId = undefined;
        this.invitationMessage = "";
        this.invitedProfile = undefined;
        this.invitedVenues = [];
        this.dayToBook = {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false
        };
        this.dayToBookVenueInvitation = {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true
        };
    }

    @action
    setInvitedProfile(profile) {
        if (profile) {
            this.invitedId = profile.id;
        }
        this.invitedProfile = profile;
    }

    @action
    setInvitationMessage(message) {
        this.invitationMessage = message;
    }

    @action
    setInvitedId(id) {
        this.invitedId = id;
    }

    @action
    setInvitedVenues(venues) {
        this.invitedVenues = venues;
    }

    @action
    toggleDayToBook(value) {
        this.dayToBook[value] = !this.dayToBook[value];
    }

    @action
    toggleDayToBookVenueInvitational(value) {
        this.dayToBookVenueInvitation[value] = !this.dayToBookVenueInvitation[value];
    }

    @action
    inviteAgentByVenue(venueId) {
        const { profile } = profileStore;

        let requestDetail = {
            primaryProfileId: venueId,
            secondaryProfileId: this.invitedId,
            type: ProfileRelationType.AgentVenue.ordinal,
            requestMessage: this.invitationMessage,
            rules: JSON.stringify(this.dayToBook),
            createdById: profile.id,
            manage: this.manage,
            manageSocialPost: this.manageSocialPost
        };

        return agent.ProfileRelationship.invite(requestDetail).then(
            action(response => {
                return response;
            })
        );
    }

    @action
    inviteAgentByStaff() {
        const currentProfile = profileStore.getCurrentProfile();
        const generation = this.staffAgentRosterGeneration;
        let requests = [];

        this.invitedVenues.forEach(venue => {
            let requestDetail = {
                primaryProfileId: venue.id,
                secondaryProfileId: this.invitedProfile.id,
                type: ProfileRelationType.AgentVenue.ordinal,
                requestMessage: this.invitationMessage,
                rules: JSON.stringify(this.dayToBook),
                createdById: currentProfile.id,
                manage: this.manage,
                manageSocialPost: this.manageSocialPost
            };
            requests.push(requestDetail);
        });

        return agent.ProfileRelationship.inviteMany(requests).then(
            action(response => {
                if (!this.isCurrentStaffRoster(currentProfile.id, generation)) return response;
                response.forEach(data => {
                    profileStore.mapAdditionalProfileData(data.primaryProfile);
                    profileStore.mapAdditionalProfileData(data.secondaryProfile);
                    data.accepted = false;
                    this.pendingAgentVenues.push(data);
                });
                return this.pendingAgentVenues;
            })
        );
    }

    @action
    inviteVenueByAgent() {
        const { currentProfile } = profileStore;

        if (currentProfile.type !== ProfileType.Agent.ordinal) {
            return "Not Agent";
        }

        let requestDetail = {
            primaryProfileId: currentProfile.id,
            secondaryProfileId: this.invitedId,
            type: ProfileRelationType.AgentVenue.ordinal,
            requestMessage: this.invitationMessage,
            rules: JSON.stringify(this.dayToBook),
            createdById: currentProfile.id,
            manage: this.manage,
            manageSocialPost: this.manageSocialPost
        };

        return agent.ProfileRelationship.invite(requestDetail).then(
            action(response => {
                profileStore.mapAdditionalProfileData(response.primaryProfile);
                profileStore.mapAdditionalProfileData(response.secondaryProfile);
                response.accepted = false;
                this.pendingAgentVenues.push(response);
                return response;
            })
        );
    }

    @action
    inviteVenueByAgentV2() {
        const { currentProfile } = profileStore;

        if (currentProfile.type !== ProfileType.Agent.ordinal) {
            return "Not Agent";
        }

        let requestDetail = {
            primaryProfileId: currentProfile.id,
            secondaryProfileId: this.invitedId,
            type: ProfileRelationType.AgentVenue.ordinal,
            requestMessage: this.invitationMessage,
            rules: JSON.stringify(this.dayToBookVenueInvitation),
            createdById: currentProfile.id,
            manage: this.manage,
            manageSocialPost: this.manageSocialPost
        };

        return agent.ProfileRelationship.invite2(requestDetail).then(
            action(response => {
                profileStore.mapAdditionalProfileData(response.primaryProfile);
                profileStore.mapAdditionalProfileData(response.secondaryProfile);
                response.accepted = false;
                this.pendingAgentVenues.push(response);
                return response;
            })
        );
    }

    @action
    removeAgentVenueRequest(request) {
        return agent.ProfileRelationship.remove(request).then(
            action(response => {
                this.pendingAgentVenues = this.pendingAgentVenues.filter(x => x.id !== response.id);
                return response;
            })
        );
    }
}

export default new AgentVenueStore();
